import { useEffect, useReducer } from 'react';
import { Capacitor } from '@capacitor/core';

export type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

declare global {
  interface Window {
    // Captured in index.html before React loads, so an early browser offer is not lost.
    __roadeInstallPrompt?: InstallPromptEvent;
  }
}

export const INSTALLED_STORAGE_KEY = 'roade-app-installed';
/** Asks the install banner to install now: the browser dialog, or the steps on iOS. */
export const OPEN_INSTALL_EVENT = 'roade:open-install';
/** Fired when the install offer or the installed flag changes outside a browser event. */
export const INSTALL_STATE_EVENT = 'roade:install-state';

export const isStandalone = () => (
  (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
  || Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
);

export const isIosDevice = () => (
  /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

export const isMarkedInstalled = () => localStorage.getItem(INSTALLED_STORAGE_KEY) === 'true';

export const setMarkedInstalled = (installed: boolean) => {
  if (installed) {
    localStorage.setItem(INSTALLED_STORAGE_KEY, 'true');
  } else {
    localStorage.removeItem(INSTALLED_STORAGE_KEY);
  }
  window.dispatchEvent(new Event(INSTALL_STATE_EVENT));
};

export const clearInstallPrompt = () => {
  delete window.__roadeInstallPrompt;
  window.dispatchEvent(new Event(INSTALL_STATE_EVENT));
};

export const requestAppInstall = () => {
  window.dispatchEvent(new Event(OPEN_INSTALL_EVENT));
};

/** True when this browser can install roade.ro right now: Android/desktop with an offer, or iOS steps. */
export const canInstallApp = () => (
  !Capacitor.isNativePlatform()
  && !isStandalone()
  && !isMarkedInstalled()
  && (isIosDevice() || Boolean(window.__roadeInstallPrompt))
);

export const useCanInstallApp = () => {
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    const events = ['beforeinstallprompt', 'appinstalled', INSTALL_STATE_EVENT];
    events.forEach(name => window.addEventListener(name, rerender));
    return () => events.forEach(name => window.removeEventListener(name, rerender));
  }, []);
  return canInstallApp();
};
